import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Button, DatePicker, InputNumber, Space, Switch, Table, Tag, Tooltip, Typography, notification,
} from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import { canManageOrgCarePrograms } from './careProgramPermissions';
import { normalizeOrgCareProgramRow, PROGRAM_STATUS_COLORS } from './careProgramNormalize';
import CareProgramUtilizationPanel from './CareProgramUtilizationPanel';
import '../../features/orgs/orgs-dashboard.css';

const { Text } = Typography;

/** PUT body must include both subscription dates or the API clears the omitted field. */
function buildOrgProgramPayload(row, patch) {
  return {
    enabled: patch.enabled ?? row.enabled,
    enabled_from: patch.enabled_from !== undefined ? patch.enabled_from : (row.enabled_from ?? null),
    enabled_to: patch.enabled_to !== undefined ? patch.enabled_to : (row.enabled_to ?? null),
    settings: patch.settings !== undefined ? patch.settings : (row.settings ?? {}),
  };
}

function mergeOrgProgramRow(previous, payload, apiRaw) {
  const fromApi = apiRaw && typeof apiRaw === 'object' && !Array.isArray(apiRaw)
    ? normalizeOrgCareProgramRow(apiRaw)
    : null;
  return normalizeOrgCareProgramRow({
    ...previous,
    ...payload,
    ...(fromApi || {}),
    enabled_from: fromApi?.enabled_from ?? payload.enabled_from ?? previous.enabled_from ?? null,
    enabled_to: fromApi?.enabled_to ?? payload.enabled_to ?? previous.enabled_to ?? null,
    settings: {
      ...(previous.settings || {}),
      ...(payload.settings || {}),
      ...(fromApi?.settings || {}),
    },
  });
}

function formatOrgProgramDate(value) {
  return value ? value.format('YYYY-MM-DD HH:mm:ss') : null;
}

function portalStatusTag(row) {
  if (row.status === 'draft') {
    return <Tag style={{ margin: 0 }}>Not published</Tag>;
  }
  if (row.portal_visible) {
    return <Tag color="success" style={{ margin: 0 }}>Live in HR portal</Tag>;
  }
  if (row.audit_visible) {
    return <Tag color="processing" style={{ margin: 0 }}>Audit access</Tag>;
  }
  if (row.enabled && !row.org_subscription_active) {
    return <Tag color="warning" style={{ margin: 0 }}>Outside org window</Tag>;
  }
  if (row.enabled && !row.program_in_active_window) {
    return <Tag color="orange" style={{ margin: 0 }}>Outside program season</Tag>;
  }
  if (row.enabled) {
    return <Tag color="default" style={{ margin: 0 }}>Enabled</Tag>;
  }
  return <Tag color="default" style={{ margin: 0 }}>Off</Tag>;
}

const OrgCareProgramsPanel = ({ orgId, orgName }) => {
  const { user } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const canManage = canManageOrgCarePrograms(user, hasAnyPermission);

  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState(null);
  const [programs, setPrograms] = useState([]);
  const programsRef = useRef(programs);
  const updateChainRef = useRef({});

  useEffect(() => {
    programsRef.current = programs;
  }, [programs]);

  const applyPrograms = useCallback((next) => {
    programsRef.current = next;
    setPrograms(next);
  }, []);

  const loadPrograms = useCallback(async () => {
    if (!orgId) {
      applyPrograms([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.listOrgCarePrograms(orgId));
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Failed to load programs' });
        applyPrograms([]);
        return;
      }
      const list = Array.isArray(parsed.data) ? parsed.data.map(normalizeOrgCareProgramRow) : [];
      applyPrograms(list);
    } catch (e) {
      notification.error({ message: e?.message || 'Network error' });
      applyPrograms([]);
    } finally {
      setLoading(false);
    }
  }, [orgId, applyPrograms]);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  const persistProgramUpdate = async (programCode, patch) => {
    const row = programsRef.current.find((p) => p.program_code === programCode);
    if (!orgId) {
      notification.error({ message: 'Organization ID missing' });
      return;
    }
    if (!row?.program_code) {
      notification.error({ message: 'Program code missing — refresh and try again' });
      return;
    }

    const payload = buildOrgProgramPayload(row, patch);
    const optimistic = programsRef.current.map((p) => (
      p.program_code === programCode ? mergeOrgProgramRow(p, payload, null) : p
    ));
    applyPrograms(optimistic);

    setSavingCode(programCode);
    try {
      const parsed = lib.parseApiEnvelope(await lib.updateOrgCareProgram(orgId, programCode, payload));
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Update failed' });
        await loadPrograms();
        return;
      }
      if (Array.isArray(parsed.data)) {
        applyPrograms(parsed.data.map(normalizeOrgCareProgramRow));
      } else if (parsed.data) {
        applyPrograms(programsRef.current.map((p) => (
          p.program_code === programCode ? mergeOrgProgramRow(p, payload, parsed.data) : p
        )));
      }
      notification.success({ message: parsed.message || 'Program settings saved' });
    } catch (e) {
      notification.error({ message: e?.message || 'Update failed' });
      await loadPrograms();
    } finally {
      setSavingCode(null);
    }
  };

  const updateProgram = (programCode, patch) => {
    const previous = updateChainRef.current[programCode] || Promise.resolve();
    const next = previous
      .catch(() => {})
      .then(() => persistProgramUpdate(programCode, patch));
    updateChainRef.current[programCode] = next;
    return next;
  };

  const enabledCount = programs.filter((p) => p.enabled).length;
  const portalCount = programs.filter((p) => p.portal_visible).length;

  const columns = [
    {
      title: 'Program',
      key: 'name',
      fixed: 'left',
      width: 180,
      render: (_, row) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{row.display_name || row.program_code}</Text>
          <div><Text type="secondary" style={{ fontSize: 11 }}>{row.program_code}</Text></div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 90,
      render: (s) => <Tag color={PROGRAM_STATUS_COLORS[s] || 'default'} style={{ margin: 0 }}>{s || 'draft'}</Tag>,
    },
    {
      title: 'Enabled',
      key: 'enabled',
      width: 80,
      align: 'center',
      render: (_, row) => {
        const isDraft = row.status === 'draft';
        return (
          <Tooltip title={isDraft ? 'Publish program first' : undefined}>
            <Switch
              size="small"
              checked={Boolean(row.enabled)}
              disabled={!canManage || isDraft || savingCode === row.program_code}
              onChange={(enabled) => updateProgram(row.program_code, { enabled })}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'HR portal',
      key: 'portal',
      width: 140,
      render: (_, row) => (
        <Tooltip title={
          row.portal_visible
            ? 'Members can see this program in the HR portal now'
            : row.audit_visible
              ? 'Season ended; org can still view utilization if there was activity'
              : 'Not visible in HR portal under current settings'
        }>
          {portalStatusTag(row)}
        </Tooltip>
      ),
    },
    {
      title: 'From',
      key: 'enabled_from',
      width: 160,
      render: (_, row) => (
        <DatePicker
          showTime
          size="small"
          style={{ width: '100%' }}
          disabled={!canManage || !row.enabled || savingCode === row.program_code}
          value={row.enabled_from ? dayjs(row.enabled_from) : null}
          allowClear
          onChange={(d) => {
            if (!d) updateProgram(row.program_code, { enabled_from: null });
          }}
          onOk={(d) => updateProgram(row.program_code, {
            enabled_from: formatOrgProgramDate(d),
          })}
        />
      ),
    },
    {
      title: 'Until',
      key: 'enabled_to',
      width: 160,
      render: (_, row) => (
        <DatePicker
          showTime
          size="small"
          style={{ width: '100%' }}
          disabled={!canManage || !row.enabled || savingCode === row.program_code}
          value={row.enabled_to ? dayjs(row.enabled_to) : null}
          allowClear
          onChange={(d) => {
            if (!d) updateProgram(row.program_code, { enabled_to: null });
          }}
          onOk={(d) => updateProgram(row.program_code, {
            enabled_to: formatOrgProgramDate(d),
          })}
        />
      ),
    },
    {
      title: 'Cycle year',
      key: 'cycle_year',
      width: 100,
      render: (_, row) => (
        <InputNumber
          min={2000}
          max={2100}
          size="small"
          style={{ width: '100%' }}
          disabled={!canManage || !row.enabled || savingCode === row.program_code}
          value={row.settings?.cycle_year ?? undefined}
          placeholder="2026"
          onChange={(v) => updateProgram(row.program_code, {
            enabled: true,
            settings: { ...(row.settings || {}), cycle_year: v ?? undefined },
          })}
        />
      ),
    },
  ];

  if (!orgId) {
    return (
      <Alert type="warning" showIcon message="Organization ID missing — cannot load care programs." />
    );
  }

  return (
    <>
      {!canManage && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="View only"
          description="You need care_programs.manage_org (or admin.*) to change org program settings."
        />
      )}
      {!loading && programs.length > 0 ? (
        <div className="org-detail-context-strip org-care-programs-strip">
          <div className="org-detail-chip">
            <span className="org-detail-chip__label">Programs</span>
            <span className="org-detail-chip__value">{programs.length}</span>
          </div>
          <div className="org-detail-chip">
            <span className="org-detail-chip__label">Org enabled</span>
            <span className="org-detail-chip__value">{enabledCount}</span>
          </div>
          <div className="org-detail-chip">
            <span className="org-detail-chip__label">HR portal live</span>
            <span className="org-detail-chip__value">{portalCount}</span>
          </div>
          {orgName ? (
            <div className="org-detail-chip org-detail-chip--grow">
              <span className="org-detail-chip__label">Organization</span>
              <span className="org-detail-chip__value">{orgName}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="org-detail-toolbar">
        <Text type="secondary" style={{ fontSize: 12 }}>
          Toggle programs and set org subscription dates. Expand a row to view utilization for enabled programs.
        </Text>
        <Button size="small" icon={<SyncOutlined spin={loading} />} onClick={loadPrograms} loading={loading}>
          Reload
        </Button>
      </div>

      <Table
        className="org-policies-table"
        rowKey={(r) => r.program_code || r.program_id}
        columns={columns}
        dataSource={programs}
        loading={loading}
        size="small"
        pagination={false}
        scroll={{ x: 920 }}
        locale={{ emptyText: 'No care programs configured' }}
        expandable={{
          expandedRowRender: (row) => (
            row.enabled && row.status === 'published' ? (
              <CareProgramUtilizationPanel
                programCode={row.program_code}
                orgId={orgId}
                orgName={orgName}
                fixedProgram
                embedded
                title={`${row.display_name || row.program_code} utilization`}
              />
            ) : (
              <Alert
                type="info"
                showIcon
                message="Enable and publish this program to view utilization for this organization."
              />
            )
          ),
          rowExpandable: (row) => Boolean(row.program_code),
        }}
      />
    </>
  );
};

export default OrgCareProgramsPanel;
